'use client';

import { Button, Label, RadioGroup, RadioGroupItem } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { Spinner } from '@shared/icons';
import { Reminder } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { useMultistepForm } from '../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { type AccountReviewReminderMutation, INTERVAL_OPTIONS } from '../AccountReviewReminderSheet';

type SelectIntervalStepProps = {
  mutation: AccountReviewReminderMutation;
};

function SelectIntervalStep({ mutation }: SelectIntervalStepProps) {
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations('my_account.settings.account_review_reminder_sheet');
  const form = useTranslations('form');
  const { watch, setValue } = useFormContext();
  const { goToNextStep, isLoading } = useMultistepForm();

  const selectedInterval = watch('interval');
  const isButtonLoading = isLoading || mutation.isPending;

  const handleSave = async () => {
    if (!selectedInterval) return;

    try {
      await mutation.mutateAsync({ interval: selectedInterval });
      await goToNextStep();
    } catch (error) {
      debug.forceError('Error saving account review reminder:', error);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-8 px-6 py-4" data-track-container="Account Review Reminder Set" data-testid="account-review-reminder-select-interval-step">
      <div className="flex flex-col gap-5 items-start justify-center w-full">
        <Reminder size="extra-extra-large" data-testid="reminder-icon" />
        <div className="flex flex-col gap-2 items-start justify-start w-full">
          <h4>{t('heading')}</h4>
          <p className="w-full">{t('subtitle')}</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 items-start justify-start w-full">
        <RadioGroup
          value={selectedInterval}
          onValueChange={value => setValue('interval', value, { shouldValidate: true })}
          className="flex flex-col gap-4 w-full"
          data-track-name="Period"
        >
          {INTERVAL_OPTIONS.map(option => (
            <div key={option.value} className="flex flex-row gap-3 h-10 items-center justify-start w-full">
              <RadioGroupItem className="size-7" value={option.value} id={`interval-${option.value}`} data-testid={`interval-${option.value}`} />
              <Label className="paragraph-explanatory hover:text-inherit" htmlFor={`interval-${option.value}`}>
                {t(option.labelKey)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="mt-auto py-4" data-track-container="Actions" data-testid="account-review-reminder-submit-button">
        <Button className="w-full" onClick={handleSave} disabled={!selectedInterval || isButtonLoading} data-track-name="Update">
          {isButtonLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={form('aria.loading')} /> : t('save')}
        </Button>
      </div>
    </div>
  );
}

export { SelectIntervalStep };
